import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MapGeoJSONFeature } from "maplibre-gl";
import { shallowEqual, useSelector } from "react-redux";
import {
  CampgroundDriveTimeSearchRequest,
  CampgroundRectangleSearchRequest,
  ErrorResponseModel,
  Feature,
  FeatureCollection,
  getCampgroundTile,
  getSearchResult,
  searchCampgroundsByDriveTime,
} from "../../api-clients/iwannacamp-api";
import { getCampgroundId } from "../../components/campground-card/campgroundUtils";
import {
  celsiusToFahrenheit,
  getMonthlyWeatherFromCampground,
} from "../../utils/weather";
import { RootState } from "../store";
import { getRoutingEngine } from "../../utils/routingEngine";
import { tilesForBounds } from "../../utils/tiles";
import {
  ProcessingStage,
  SearchProgress,
} from "../../hooks/useSearchProgress";

/** How one tile's request turned out, for the admin tile-grid overlay. */
export interface TileFetchDebug {
  /** "z/x/y", the tile's address. */
  key: string;
  z: number;
  x: number;
  y: number;
  count: number;
  /** The server hit its per-tile cap, so this tile is under-reporting. */
  limited: boolean;
  /**
   * The cap this response was built under, which a cached body carries from
   * whenever it was built. Two tiles in one view disagreeing means one is
   * stale. 0 when the request produced no response.
   */
  limit: number;
  /** 0 when the request produced no response at all -- an abort looks like this. */
  status: number;
  ms: number;
}

export type SearchResultsState = {
  // The search query
  query?:
    | CampgroundDriveTimeSearchRequest
    | (CampgroundRectangleSearchRequest & SearchByRectangleExtra);

  // The results of the search
  unfilteredCampgrounds?: FeatureCollection;
  campgrounds?: FeatureCollection;
  visibleCampgrounds?: MapGeoJSONFeature[];
  isochrone?: FeatureCollection;

  // Filter settings
  filters?: {
    amenities?: string[];
    temperatureLoHiMon?: [number, number, number];
    favorites?: string[];
  };

  // Status of the search
  status: "idle" | "pending" | "success" | "error";
  initialLoad?: boolean; // Whether search was triggered by initial load (true), or by user action (false)
  error?: string;
  resultsLimited: boolean;
  /** Per-tile outcomes of the last tile search. Admin debugging only. */
  tileDebug?: TileFetchDebug[];

  // Queue state for drive-time searches
  jobId?: string;
  queuePosition?: number;
  etaSeconds?: number;
  processingStage?: ProcessingStage;
};

const initialState: SearchResultsState = {
  status: "idle",
  resultsLimited: false,
};

export interface SearchByDriveTimePayload {
  latDeg: number;
  lonDeg: number;
  driveTimeMins: number;
}

const sortCampgrounds = (campgrounds: MapGeoJSONFeature[]) => {
  return campgrounds.sort((a, b) => {
    // Sort by number of amenities
    const aAmenities = Object.keys(a.properties!).filter(
      (key) => a.properties![key] === "yes"
    ).length;
    const bAmenities = Object.keys(b.properties!).filter(
      (key) => b.properties![key] === "yes"
    ).length;
    return bAmenities - aAmenities;
  });
};

const filterCampgrounds = (
  campgrounds: Feature[],
  filters: SearchResultsState["filters"]
) => {
  let filteredCampgrounds = campgrounds;

  // Filter by amenities
  if (filters?.amenities) {
    filteredCampgrounds = filteredCampgrounds.filter((campground) => {
      return filters?.amenities?.every((amenity) => {
        return campground.properties![amenity] === "yes";
      });
    });
  }

  // Filter by temperature range
  if (filters?.temperatureLoHiMon) {
    filteredCampgrounds = filteredCampgrounds.filter((campground) => {
      const { tmin: currentTmin, tmax: currentTmax } =
        getMonthlyWeatherFromCampground(campground);

      if (!currentTmin || !currentTmax || filters === undefined) return false;

      const tempMonth = filters.temperatureLoHiMon![2];
      return (
        celsiusToFahrenheit(currentTmin[tempMonth]) >=
          filters.temperatureLoHiMon![0] &&
        celsiusToFahrenheit(currentTmax[tempMonth]) <=
          filters.temperatureLoHiMon![1]
      );
    });
  }

  // Filter by favorites
  if (filters?.favorites) {
    filteredCampgrounds = filteredCampgrounds.filter((campground) => {
      return filters?.favorites?.includes(getCampgroundId(campground));
    });
  }

  return filteredCampgrounds;
};

const campgroundsEqual = (
  a: MapGeoJSONFeature[] | Feature[] | undefined,
  b: MapGeoJSONFeature[] | Feature[] | undefined
) => {
  // Compare lengths of campgrounds
  if (a?.length !== b?.length) return false;

  // Compare names of campgrounds
  return (
    a?.every((campground, index) => {
      return campground.properties?.name === b?.[index].properties?.name;
    }) ?? false
  );
};

// Helper function to only create new campgrounds object if features actually changed
const updateCampgroundsIfChanged = (
  currentCampgrounds: FeatureCollection | undefined,
  newFeatures: Feature[],
  template: FeatureCollection
): FeatureCollection => {
  // If features haven't changed, return the existing object to preserve reference
  if (campgroundsEqual(currentCampgrounds?.features, newFeatures)) {
    return currentCampgrounds!;
  }

  // Features changed, create new object
  return {
    ...template,
    features: newFeatures,
  };
};

const campgroundSearch = createSlice({
  name: "campgroundSearch",
  initialState,
  reducers: {
    prepForSearch: (
      state,
      action: PayloadAction<
        CampgroundDriveTimeSearchRequest | CampgroundRectangleSearchRequest
      >
    ) => {
      state.campgrounds = undefined;
      state.unfilteredCampgrounds = undefined;
      state.visibleCampgrounds = undefined;
      state.isochrone = undefined;
      state.query = action.payload;
      state.initialLoad = false;
    },
    setVisibleCampgrounds: (
      state,
      action: PayloadAction<MapGeoJSONFeature[] | undefined>
    ) => {
      // Handle empty arrays - only update if current value isn't already empty
      if (action.payload?.length == 0) {
        if (state.visibleCampgrounds?.length !== 0) {
          state.visibleCampgrounds = [];
        }
        return;
      }

      const sortedCampgrounds = sortCampgrounds(action.payload ?? []);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (campgroundsEqual(state.visibleCampgrounds, sortedCampgrounds)) return;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.visibleCampgrounds = sortedCampgrounds;
    },
    setFilters: (state, action: PayloadAction<typeof initialState.filters>) => {
      state.filters = action.payload;

      // Apply filters
      if (state.unfilteredCampgrounds) {
        const newFeatures =
          state.filters === undefined
            ? state.unfilteredCampgrounds.features
            : filterCampgrounds(
                state.unfilteredCampgrounds.features,
                state.filters
              );
        state.campgrounds = updateCampgroundsIfChanged(
          state.campgrounds,
          newFeatures,
          state.unfilteredCampgrounds
        );
      }
    },
    resetSearch: (state) => {
      // Reset most of the state, but keep campgrounds so they don't disappear from the map in-between searches
      const newState = { ...initialState };
      newState.initialLoad = true;
      newState.campgrounds = state.campgrounds;
      newState.unfilteredCampgrounds = state.unfilteredCampgrounds;
      // @ts-ignore
      newState.visibleCampgrounds = state.visibleCampgrounds;
      newState.query = state.query;
      return newState;
    },
    // Update search progress from SSE
    updateSearchProgress: (state, action: PayloadAction<SearchProgress>) => {
      const { status, position, eta_seconds, stage } = action.payload;

      if (position !== undefined) {
        state.queuePosition = position;
      }
      if (eta_seconds !== undefined) {
        state.etaSeconds = eta_seconds;
      }
      if (stage !== undefined) {
        state.processingStage = stage;
      }

      // Update status based on progress
      if (status === "error") {
        state.status = "error";
        state.error = action.payload.error || "Search failed";
      }
      // Note: "completed" is handled by fetchSearchResult
    },
    // Clear queue state
    clearQueueState: (state) => {
      state.jobId = undefined;
      state.queuePosition = undefined;
      state.etaSeconds = undefined;
      state.processingStage = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // searchByDriveTime - now returns 202 with job info
      .addCase(searchByDriveTime.pending, (state) => {
        state.status = "pending";
        // Clear previous queue state
        state.jobId = undefined;
        state.queuePosition = undefined;
        state.etaSeconds = undefined;
        state.processingStage = undefined;
      })
      .addCase(searchByDriveTime.rejected, (state, action) => {
        if (action.error.message === SEARCH_INTERRUPTED_SIGNAL) return;
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(searchByDriveTime.fulfilled, (state, action) => {
        // Handle 202 Accepted - job enqueued
        if (action.payload.response?.status === 202) {
          // @ts-ignore - The type will be updated after regenerating client
          const data = action.payload.data as {
            job_id: string;
            queue_position: number;
            estimated_wait_seconds: number;
          };
          state.jobId = data.job_id;
          state.queuePosition = data.queue_position;
          state.etaSeconds = data.estimated_wait_seconds;
          // Status remains "pending" - waiting for SSE updates
          return;
        }

        // Handle errors
        if (action.payload.response?.status !== 200) {
          state.status = "error";
          state.error = (action.payload.error as ErrorResponseModel).message;
          return;
        }
      })
      // fetchSearchResult - gets the completed search results
      .addCase(fetchSearchResult.pending, (_state) => {
        // Keep status as pending while fetching result
      })
      .addCase(fetchSearchResult.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(fetchSearchResult.fulfilled, (state, action) => {
        if (action.payload.response?.status !== 200) {
          state.status = "error";
          state.error = (action.payload.error as ErrorResponseModel).message;
          return;
        }

        if (action.payload.data?.campgrounds?.features) {
          state.status = "success";
          const sortedCampgrounds = action.payload.data?.campgrounds?.features
            .sort((a, b) => {
              // Sort by number of amenities
              const aAmenities = Object.keys(a.properties!).filter(
                (key) => a.properties![key] === "yes"
              ).length;
              const bAmenities = Object.keys(b.properties!).filter(
                (key) => b.properties![key] === "yes"
              ).length;
              return bAmenities - aAmenities;
            })
            .reverse();

          state.unfilteredCampgrounds = {
            ...action.payload.data?.campgrounds,
            features: sortedCampgrounds!,
          };

          // Apply filters (or use all campgrounds if no filters)
          const newFeatures =
            state.filters === undefined
              ? state.unfilteredCampgrounds.features
              : filterCampgrounds(
                  state.unfilteredCampgrounds.features,
                  state.filters
                );
          state.campgrounds = updateCampgroundsIfChanged(
            state.campgrounds,
            newFeatures,
            state.unfilteredCampgrounds
          );
          state.isochrone = action.payload.data?.drivingRange;

          // Clear queue state
          state.jobId = undefined;
          state.queuePosition = undefined;
          state.etaSeconds = undefined;
          state.processingStage = undefined;
        } else {
          state.status = "error";
          state.error = "No campgrounds found";
        }
      })
      // searchByRectnalge
      .addCase(searchByRectangle.pending, (state, action) => {
        state.status = "pending";
        state.query = action.meta.arg;
        state.initialLoad = action.meta.arg.initialLoad;
      })
      .addCase(searchByRectangle.rejected, (state, action) => {
        if (action.error.message === SEARCH_INTERRUPTED_SIGNAL) return;
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(searchByRectangle.fulfilled, (state, action) => {
        state.status = "success";
        state.resultsLimited = action.payload.resultsLimited;
        state.tileDebug = action.payload.tileDebug;
        state.unfilteredCampgrounds = {
          type: "FeatureCollection",
          features: action.payload.features,
        };

        const newFeatures =
          state.filters === undefined
            ? state.unfilteredCampgrounds.features
            : filterCampgrounds(
                state.unfilteredCampgrounds.features,
                state.filters
              );
        state.campgrounds = updateCampgroundsIfChanged(
          state.campgrounds,
          newFeatures,
          state.unfilteredCampgrounds
        );
      });
  },
});

const { prepForSearch } = campgroundSearch.actions;

export const {
  setVisibleCampgrounds,
  resetSearch,
  setFilters,
  updateSearchProgress,
  clearQueueState,
} = campgroundSearch.actions;

export default campgroundSearch.reducer;

// Simple selectors for primitive values
const selectFilters = (state: RootState) => state.campgroundSearch.filters;

const selectQuery = (state: RootState) => state.campgroundSearch.query;

const selectIsochrone = (state: RootState) => state.campgroundSearch.isochrone;

const selectStatus = (state: RootState) => state.campgroundSearch.status;

const selectIsInitialLoad = (state: RootState) =>
  state.campgroundSearch.initialLoad;

const selectError = (state: RootState) => state.campgroundSearch.error;

const selectResultsLimited = (state: RootState) =>
  state.campgroundSearch.resultsLimited;

// Simple selectors for campgrounds
// The reducer preserves object references via updateCampgroundsIfChanged,
// and the hooks have equality functions that prevent unnecessary re-renders
const selectCampgrounds = (state: RootState) =>
  state.campgroundSearch.campgrounds;

const selectVisibleCampgrounds = (state: RootState) =>
  state.campgroundSearch.visibleCampgrounds;

const createUseSelector =
  <T>(
    selector: (state: RootState) => T,
    equalityFn?: (left: T, right: T) => boolean
  ) =>
  () =>
    useSelector(selector, equalityFn);

export const useFilters = createUseSelector(selectFilters, shallowEqual);
export const useQuery = createUseSelector(selectQuery, shallowEqual);
export const useIsochrone = createUseSelector(selectIsochrone, shallowEqual);
export const useStatus = createUseSelector(selectStatus);
export const useIsInitialLoad = createUseSelector(selectIsInitialLoad);
export const useError = createUseSelector(selectError);
export const useCampgrounds = createUseSelector(selectCampgrounds, (a, b) =>
  campgroundsEqual(a?.features, b?.features)
);
export const useVisibleCampgrounds = createUseSelector(
  selectVisibleCampgrounds,
  campgroundsEqual
);
export const useResultsLimited = createUseSelector(selectResultsLimited);

const selectTileDebug = (state: RootState) => state.campgroundSearch.tileDebug;
export const useTileDebug = createUseSelector(selectTileDebug, shallowEqual);

// Queue state selectors
const selectJobId = (state: RootState) => state.campgroundSearch.jobId;
const selectQueuePosition = (state: RootState) =>
  state.campgroundSearch.queuePosition;
const selectEtaSeconds = (state: RootState) => state.campgroundSearch.etaSeconds;
const selectProcessingStage = (state: RootState) =>
  state.campgroundSearch.processingStage;

export const useJobId = createUseSelector(selectJobId);
export const useQueuePosition = createUseSelector(selectQueuePosition);
export const useEtaSeconds = createUseSelector(selectEtaSeconds);
export const useProcessingStage = createUseSelector(selectProcessingStage);

// Also export raw selectors for use in components
export { selectJobId };

let searchAbortController: AbortController | null = null;
const SEARCH_INTERRUPTED_SIGNAL = "interrupted";

export const searchByDriveTime = createAsyncThunk(
  "campgroundSearch/searchByDriveTime",
  async (payload: CampgroundDriveTimeSearchRequest, { dispatch }) => {
    if (searchAbortController) {
      searchAbortController.abort(SEARCH_INTERRUPTED_SIGNAL);
    }
    searchAbortController = new AbortController();
    const signal = searchAbortController.signal;

    dispatch(prepForSearch(payload));
    // Honour ?engine=reachd. Injected here rather than at each call site so
    // every drive-time search picks it up, and omitted entirely when unset so
    // the request body is byte-identical to before.
    //
    // The intersection type this used to carry is gone: the client has been
    // regenerated and `engine` is on CampgroundDriveTimeSearchRequest now,
    // which is exactly what the old comment said would retire it.
    const engine = getRoutingEngine();
    const body: CampgroundDriveTimeSearchRequest = engine
      ? { ...payload, engine }
      : payload;
    return searchCampgroundsByDriveTime({
      body,
      signal,
    });
  }
);

export type SearchByRectangleExtra = {
  initialLoad?: boolean;
  /** Map zoom, used to pick the tile level. */
  mapZoom?: number;
};

export const searchByRectangle = createAsyncThunk(
  "campgroundSearch/searchByRectangle",
  async (
    payload: CampgroundRectangleSearchRequest & SearchByRectangleExtra
  ) => {
    if (searchAbortController) {
      searchAbortController.abort(SEARCH_INTERRUPTED_SIGNAL);
    }
    searchAbortController = new AbortController();
    const signal = searchAbortController.signal;

    // The viewport is cut into cacheable tiles rather than sent as raw float
    // bounds. Requests go out together: a view costs one round trip no matter
    // how many tiles it spans, and tiles already in the browser's HTTP cache
    // cost nothing at all.
    const tiles = tilesForBounds(
      {
        west: payload.nwLonDeg,
        north: payload.nwLatDeg,
        east: payload.seLonDeg,
        south: payload.seLatDeg,
      },
      payload.mapZoom ?? 10
    );

    const responses = await Promise.all(
      tiles.map(async (t) => {
        const startedAt = performance.now();
        const response = await getCampgroundTile({
          path: { z: t.z, x: t.x, y: t.y },
          signal,
        });
        return { response, ms: Math.round(performance.now() - startedAt) };
      })
    );

    const features: Feature[] = [];
    // Per-tile outcomes, for the admin tile-grid overlay: which tile hit the
    // server's cap, which one failed, and how long each took. The batch-wide
    // resultsLimited flag below cannot answer "which".
    const tileDebug: TileFetchDebug[] = [];
    let resultsLimited = false;
    let ok = 0;
    for (const [i, { response: r, ms }] of responses.entries()) {
      const t = tiles[i];
      const usable = r.response?.status === 200 && !!r.data;
      const count = usable ? (r.data?.campgrounds?.features?.length ?? 0) : 0;
      tileDebug.push({
        key: `${t.z}/${t.x}/${t.y}`,
        z: t.z,
        x: t.x,
        y: t.y,
        count,
        limited: !!r.data?.tile_limited,
        // The cap THIS response was built under, which is not necessarily the
        // one the server runs now: a body served from cache carries the older
        // cap, and that mismatch is the only way to tell a stale tile from a
        // genuinely truncated one.
        limit: r.data?.tile_limit ?? 0,
        // Zero when the request never produced a response at all -- an abort
        // resolves as {error, response: undefined} rather than rejecting.
        status: r.response?.status ?? 0,
        ms,
      });
      if (!usable) continue;
      ok++;
      features.push(...(r.data?.campgrounds?.features ?? []));
      if (r.data?.tile_limited) resultsLimited = true;
    }

    // This must run before the outage check below, and unconditionally --
    // not only when every tile failed. The generated client resolves an
    // aborted request as {error, response: undefined} rather than rejecting,
    // so an aborted tile looks exactly like a failed one from here. Caching
    // makes a *partially* resolved batch the common case: a cache hit
    // resolves in ~1ms while a cold tile takes 100-800ms, so "some tiles
    // landed, the rest are still in flight" is normal when the user pans
    // again. If the ok===0 check below ran first, such a batch would skip
    // the outage branch (ok > 0) and fulfil with only whichever tiles
    // happened to land, labelled "success" -- silently committing a
    // half-visited view as the final state whenever the superseding batch
    // itself fails.
    if (signal.aborted) {
      throw new Error(SEARCH_INTERRUPTED_SIGNAL);
    }

    // One failed tile out of several is a gap in the map, not a failed search,
    // so it is tolerated. All of them failing is a failed search, and must
    // reject rather than report success with nothing found -- otherwise an
    // outage renders as "no campgrounds here".
    if (ok === 0 && tiles.length > 0) {
      throw new Error("Failed to load campgrounds for this area");
    }

    // One zoom level partitions the plane and campgrounds are points, so a
    // campground appears in exactly one tile -- except one sitting on a shared
    // tile edge, which both neighbours return. PostGIS `&&` compares a
    // float4-rounded box, so the server cannot cheaply exclude it without
    // shrinking tiles by ~3 m and dropping campgrounds near edges outright.
    // Deduping here is the cheap half of that trade.
    //
    // Only dedupe features that actually carry a sqid: keying on `undefined`
    // would collapse any sqid-less features into one another, which is
    // exactly the "campground vanishes" failure the dedupe exists to avoid.
    const bySqid = new Map<string, Feature>();
    const deduped: Feature[] = [];
    for (const f of features) {
      const sqid = f.properties?.sqid;
      if (typeof sqid !== "string") {
        deduped.push(f);
        continue;
      }
      if (!bySqid.has(sqid)) {
        bySqid.set(sqid, f);
        deduped.push(f);
      }
    }

    // Re-rank across tiles to restore a single global ordering. Tie-break on
    // sqid so identical requests return identical order -- otherwise ties
    // order by whichever tile's response happened to land first. This is
    // NOT the same total order as the backend's `amenity_count DESC, id`:
    // sqids encode `id` rather than being monotonic in it, so the two orders
    // genuinely differ on ties. That is fine here -- order only reaches the
    // GeoJSON source, whose layers render with icon-allow-overlap, and
    // determinism (not matching the server's order) is the property caching
    // depends on.
    deduped.sort((a, b) => {
      // `|| 0` rather than `?? 0`: a non-numeric amenity_count would make
      // Number(...) produce NaN, and a comparator that can return NaN leaves
      // the sort order unspecified -- breaking the determinism above.
      const av = Number(a.properties?.amenity_count ?? 0) || 0;
      const bv = Number(b.properties?.amenity_count ?? 0) || 0;
      if (av !== bv) return bv - av;
      const aSqid = String(a.properties?.sqid ?? "");
      const bSqid = String(b.properties?.sqid ?? "");
      return aSqid < bSqid ? -1 : aSqid > bSqid ? 1 : 0;
    });

    return { features: deduped, resultsLimited, tileDebug };
  }
);

// Fetch completed search result by job ID
export const fetchSearchResult = createAsyncThunk(
  "campgroundSearch/fetchSearchResult",
  async (jobId: string) => {
    return getSearchResult({
      path: { job_id: jobId },
    });
  }
);
